import { FunctionComponent, useEffect, useState } from 'react';

import { Alert } from '@mui/material';
import { StorefrontOutlined, TravelExploreOutlined, VisibilityOffOutlined } from '@mui/icons-material';

import { SelectionTileGroup } from '@dw/components/core';
import type { ISelectionTileOption } from '@dw/components/core';
import { useAuth } from '@dw/hooks';
import { useUpdateDanceSchoolAppSettingsMutation } from '@dw/hooks/api/DanceSchoolApi';
import { DANCE_SCHOOL_DIRECTORY_LISTING } from '@dw/models/danceSchool';
import { USER_ROLE } from '@dw/models/danceSchoolUser';

import { SettingsSection } from '../shared';
import { PhoneFrame, DirectoryPreview } from '../illustrations';

import { useDirectoryPreview } from './useDirectoryPreview';

const LISTING_OPTIONS: ISelectionTileOption<DANCE_SCHOOL_DIRECTORY_LISTING>[] = [
  {
    value: DANCE_SCHOOL_DIRECTORY_LISTING.LISTED,
    label: 'Anyone can find us',
    caption: 'Listed for dancers browsing for a studio',
    icon: <TravelExploreOutlined color='primary' />
  },
  {
    value: DANCE_SCHOOL_DIRECTORY_LISTING.HIDDEN,
    label: 'Only people we invite',
    caption: 'Hidden from the list in the app',
    icon: <VisibilityOffOutlined color='primary' />
  }
];

/**
 * Whether the school turns up when a dancer goes looking for a studio.
 *
 * Saved the moment a tile is picked rather than behind a Save button — one choice with an immediate
 * picture of its result, the same as the appearance tiles below. The tile state is held locally and
 * re-synced from the school so the list moves under the pointer instead of after the round trip.
 *
 * <b>Hiding is not the same as closing enrolments.</b> It removes the school from the app's
 * directory and nothing else: the families already at the school keep everything, an enrolment link
 * or the website widget still works, and whether new enrolments are accepted at all stays with
 * Enrolment Open under the Enrolment tab.
 */
export const DirectoryListingSection: FunctionComponent = () => {

  const { danceSchool, userHasRole } = useAuth();
  const { palette, schoolName, imageUrl } = useDirectoryPreview();

  const canEdit = userHasRole(USER_ROLE.OWNER, USER_ROLE.ADMIN);

  const savedListing = danceSchool?.directoryListing ?? DANCE_SCHOOL_DIRECTORY_LISTING.LISTED;
  const [listing, setListing] = useState<DANCE_SCHOOL_DIRECTORY_LISTING>(savedListing);

  const { mutate: updateAppSettings, isPending: isSaving } = useUpdateDanceSchoolAppSettingsMutation();

  useEffect(() => setListing(savedListing), [savedListing]);

  const handleChange = (value: DANCE_SCHOOL_DIRECTORY_LISTING) => {
    if (!canEdit || value === listing) {
      return;
    }

    setListing(value);
    updateAppSettings({ directoryListing: value });
  };

  return (
    <SettingsSection
      icon={<StorefrontOutlined color='primary' />}
      title='Finding your school'
      caption='Whether dancers who have not joined yet can find you in the app.'
      illustration={(
        <PhoneFrame palette={palette} width={188}>
          <DirectoryPreview
            palette={palette}
            schoolName={schoolName}
            imageUrl={imageUrl}
            /* The tile being considered, not the saved value — the list has to move as it is clicked. */
            isListed={listing === DANCE_SCHOOL_DIRECTORY_LISTING.LISTED}
            emphasis='listing'
          />
        </PhoneFrame>
      )}
      note={listing === DANCE_SCHOOL_DIRECTORY_LISTING.LISTED
        ? 'Dancers browsing the app can find you and follow the link to your website to enrol.'
        : 'Hidden. Your families keep everything they have, and your enrolment link and website still work — this only takes you out of the list dancers browse.'}
    >
      <SelectionTileGroup<DANCE_SCHOOL_DIRECTORY_LISTING>
        label='Who can find your school'
        options={LISTING_OPTIONS}
        value={listing}
        onChange={handleChange}
        disabled={!canEdit || isSaving}
      />

      {!canEdit && <Alert severity='info'>Only an owner or admin can change who can find your school.</Alert>}
    </SettingsSection>
  );
};
